import * as Sentry from '@sentry/react';
import { API_BASE_URL } from '../config/api';
import type { EventType, EventRequest } from '@daddytime/events';
import type {
  SessionFeedbackSubmitData,
  RequestSessionSubmitData,
  GeneralFeedbackSubmitData,
} from '@daddytime/events';
import { FORM_NAMES } from '@daddytime/events';

// Declare window.dataLayer for GTM and E2E test context
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    __e2e_test_context__?: {
      is_test: true;
      test_run_id: string;
      test_id: string;
    };
    __e2e_analytics_pending__?: number;
    __e2e_analytics_buffer__?: Array<{
      eventType: EventType;
      payload: Partial<EventRequest>;
      timestamp: number;
    }>;
    __analytics__?: Analytics;
  }
}

// Type definitions for analytics events
export interface EventMetadata {
  [key: string]: unknown;
}

export interface UserProperties {
  id: string;
  type?: string;
  [key: string]: unknown;
}

/**
 * FormTracker - Strongly-typed form event tracker
 *
 * Each form gets its own instance with type-safe methods for tracking
 * form interactions (start, abandon, submit).
 *
 * @template TSubmitData - The form's submit data type (excluding form_name/form_action)
 */
class FormTracker<TSubmitData extends { form_text: string }> {
  constructor(
    private readonly formName: string,
    private readonly analytics: Analytics
  ) {}

  /**
   * Track when user starts typing in the form
   * @param additionalData - Optional context (e.g., session_id for session feedback)
   */
  start(additionalData?: Record<string, unknown>): void {
    this.analytics.push('form_interaction', {
      form_name: this.formName,
      form_action: 'start',
      ...additionalData
    });
  }

  /**
   * Track when user abandons the form without submitting
   * @param data - Optional data about what was entered before abandoning
   */
  abandon(data?: Partial<Omit<TSubmitData, 'form_name' | 'form_action'>>): void {
    this.analytics.push('form_interaction', {
      form_name: this.formName,
      form_action: 'abandon',
      ...data
    });
  }

  /**
   * Track form submission
   * @param data - Form-specific submission data (form_name and form_action are added automatically)
   */
  submit(data: Omit<TSubmitData, 'form_name' | 'form_action'>): void {
    this.analytics.push('form_interaction', {
      form_name: this.formName,
      form_action: 'submit',
      ...data
    });
  }
}

/**
 * ConversionTracker - Strongly-typed conversion event tracker
 *
 * Provides type-safe methods for tracking common conversion events.
 * Automatically injects conversion_type and default values.
 */
class ConversionTracker {
  constructor(private readonly analytics: Analytics) {}

  /**
   * Track magic link sent conversion
   * @param data - Email domain and optional metadata
   */
  magicLinkSent(data: { email_domain: string; [key: string]: unknown }): void {
    this.analytics.push('conversion', {
      conversion_type: 'magic_link_sent',
      conversion_value: 1,
      currency: 'USD',
      ...data
    });
  }

  /**
   * Track user authenticated conversion
   * @param data - Authentication method, email domain, and optional metadata
   */
  userAuthenticated(data: { method: string; email_domain?: string; [key: string]: unknown }): void {
    this.analytics.push('conversion', {
      conversion_type: 'user_authenticated',
      conversion_value: 1,
      currency: 'USD',
      ...data
    });
  }

  /**
   * Track request submission conversion
   * @param data - Form text, request length, and optional metadata
   */
  requestSubmission(data: { form_text: string; request_length?: number; [key: string]: unknown }): void {
    this.analytics.push('conversion', {
      conversion_type: 'request_submission',
      conversion_value: 1,
      currency: 'USD',
      ...data
    });
  }

  /**
   * Track a custom conversion event
   * @param data - Conversion type, value, and optional metadata
   */
  track(data: { conversion_type: string; conversion_value?: number; [key: string]: unknown }): void {
    this.analytics.push('conversion', {
      conversion_value: 0,
      currency: 'USD',
      ...data
    });
  }
}

/**
 * Analytics utility for GA4 + GTM integration
 * Provides a clean interface for tracking events across the application
 */
export class Analytics {
  private dataLayer: Record<string, unknown>[];
  private isEnabled: boolean;
  private debug: boolean;
  private apiEndpoint: string;
  private sessionId: string;

  /**
   * Strongly-typed form trackers
   * Each form has its own tracker instance with type-safe methods
   */
  public readonly forms = {
    session_feedback: new FormTracker<SessionFeedbackSubmitData>(
      FORM_NAMES.SESSION_FEEDBACK,
      this
    ),
    request_session: new FormTracker<RequestSessionSubmitData>(
      FORM_NAMES.REQUEST_SESSION,
      this
    ),
    general_feedback: new FormTracker<GeneralFeedbackSubmitData>(
      FORM_NAMES.GENERAL_FEEDBACK,
      this
    ),
  } as const;

  /**
   * Strongly-typed conversion tracker
   * Provides methods for tracking common conversion events
   */
  public readonly conversions = new ConversionTracker(this);

  constructor() {
    this.dataLayer = window.dataLayer || [];
    window.dataLayer = this.dataLayer;
    this.isEnabled = true;
    // Enable debug logs based on VITE_ENABLE_ANALYTICS_LOGS env var
    // Defaults to true for dev mode if not explicitly set
    this.debug = import.meta.env.VITE_ENABLE_ANALYTICS_LOGS === 'true' ||
                 (import.meta.env.VITE_ENABLE_ANALYTICS_LOGS === undefined && import.meta.env.DEV);
    // Use the URL API to construct the events endpoint
    this.apiEndpoint = new URL('events', API_BASE_URL).href;
    this.sessionId = this.generateSessionId();
  }

  /**
   * Identify the signed-in user to GA4 via the dataLayer.
   *
   * Pushes user_id and (optionally) user_properties so GA4 can stitch
   * sessions across devices and segment by user attributes. dataLayer-only
   * — does NOT go through the typed events backend, since user identity
   * isn't an EventType.
   *
   * Pass a stable, non-PII identifier (e.g. Cognito sub). Email addresses
   * violate GA4's terms of service — don't pass them here.
   */
  identify(userId: string, properties?: Record<string, unknown>): void {
    if (!this.isEnabled) return;
    if (this.getTestMetadata()) return; // Skip in E2E test mode
    const payload: Record<string, unknown> = { user_id: userId };
    if (properties) payload.user_properties = properties;
    this.dataLayer.push(payload);
    if (this.debug) {
      console.log('[Analytics] User identified:', userId, properties);
    }
  }

  /**
   * Initialize GTM (call this once in your app)
   */
  init(gtmId: string): void {
    if (!gtmId) {
      console.warn('Analytics: No GTM ID provided');
      return;
    }

    // GTM initialization
    this.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    if (this.debug) {
      console.log('Analytics: Initialized with GTM ID:', gtmId);
    }
  }

  /**
   * Display a visible error message on the page (for test debugging)
   * Makes errors visible in Playwright screenshots even if page errors aren't caught
   */
  private displayTestError(message: string): void {
    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: #ff0000;
      color: white;
      padding: 20px;
      z-index: 999999;
      font-family: monospace;
      font-size: 14px;
      white-space: pre-wrap;
      word-wrap: break-word;
    `;
    errorDiv.textContent = `🚨 E2E TEST ERROR 🚨\n\n${message}`;
    document.body.prepend(errorDiv);
  }

  /**
   * Get test context metadata if running in E2E test environment
   */
  private getTestMetadata(): { metadata: { is_test: true; test_run_id: string; test_id: string } } | null {
    // Validate: if automation is detected (Playwright, Selenium, etc.), test context SHOULD be set
    if (navigator.webdriver && !window.__e2e_test_context__) {
      const errorMessage =
        'window.__e2e_test_context__ not set in automated test. ' +
        'navigator.webdriver is true but test context is missing. ' +
        'Did you forget to use the test-context fixture? ' +
        'Import from "./fixtures/test-context" instead of "@playwright/test".';

      // Display visible error on page so it appears in screenshots
      this.displayTestError(errorMessage);

      // Throw error to stop execution and prevent events from being sent
      throw new Error(errorMessage);
    }

    if (window.__e2e_test_context__) {
      if (this.debug) {
        console.log('[Analytics] Test context detected:', window.__e2e_test_context__);
      }
      return {
        metadata: {
          is_test: true,
          test_run_id: window.__e2e_test_context__.test_run_id,
          test_id: window.__e2e_test_context__.test_id,
        }
      };
    }

    return null;
  }

  /**
   * Generic event pusher - your single source of truth
   */
  push(eventName: EventType, data: EventMetadata = {}): void {
    if (!this.isEnabled) return;

    // Auto-inject test metadata if running in E2E test environment
    const testMetadata = this.getTestMetadata();
    const enrichedData = testMetadata
      ? { ...data, ...testMetadata }
      : data;

    const eventData = {
      event: eventName,
      ...enrichedData,
      timestamp: Date.now(),
      user_agent: navigator.userAgent
    };

    // Skip GTM/dataLayer for test events
    const metadata = enrichedData.metadata as { is_test?: boolean } | undefined;
    const isTestEvent = metadata?.is_test === true;
    if (!isTestEvent) {
      this.dataLayer.push(eventData);
    }

    // Increment pending counter synchronously (before any async operations)
    // This ensures counter increments in current tick, not microtask queue
    if (isTestEvent) {
      const before = window.__e2e_analytics_pending__ || 0;
      window.__e2e_analytics_pending__ = before + 1;
      console.log(`[Analytics] Counter incremented: ${before} → ${window.__e2e_analytics_pending__} (${eventName})`);
    }

    // Fire-and-forget for both production and test mode
    const promise = this.sendToBackend(eventName, enrichedData);

    // Attach decrement handler to promise completion for test mode
    if (isTestEvent) {
      promise.finally(() => {
        const before = window.__e2e_analytics_pending__ || 0;
        window.__e2e_analytics_pending__ = Math.max(0, before - 1);
        console.log(`[Analytics] Counter decremented: ${before} → ${window.__e2e_analytics_pending__} (${eventName})`);
      });
    }

    if (this.debug) {
      console.log('Analytics Event:', eventData, isTestEvent ? '(test event - skipped GTM)' : '');
    }
  }

  /**
   * Send event to backend API with exponential backoff retry for throttling
   */
  async sendToBackend(eventType: EventType, data: EventMetadata): Promise<void> {
    if (!this.apiEndpoint) {
      if (this.debug) {
        console.log('[Analytics] No API endpoint configured, skipping backend tracking');
      }
      return;
    }

    const maxRetries = 6;
    const baseDelay = 300; // 300ms, 600ms, 1200ms, 2400ms, 4800ms, 9600ms = ~18.9s total

    // Extract metadata if present (test markers live here now)
    const { metadata, ...eventData } = data as any;

    const payload: Partial<EventRequest> = {
      event_type: eventType,
      data: eventData,
      session_id: this.sessionId,
      user_id: this.getUserId()
    };

    // Add metadata to payload if present (contains test markers)
    if (metadata) {
      payload.metadata = metadata;
      if (this.debug || metadata.is_test) {
        console.log('[Analytics] Sending event with metadata:', { event_type: eventType, metadata });
      }
    }

    // Buffer event in test mode for verification
    const isTestEvent = metadata?.is_test === true;
    if (isTestEvent) {
      if (!window.__e2e_analytics_buffer__) {
        window.__e2e_analytics_buffer__ = [];
      }
      window.__e2e_analytics_buffer__.push({
        eventType,
        payload: JSON.parse(JSON.stringify(payload)), // Deep clone to capture exact payload
        timestamp: Date.now()
      });
      console.log('[Analytics] Event buffered for test verification:', eventType);
    }

    // Helper to calculate exponential backoff delay
    const getRetryDelay = (attempt: number) => baseDelay * Math.pow(2, attempt);

    // Helper to determine if error is retryable
    const isRetryableError = (statusCode?: number): boolean => {
      if (!statusCode) return true; // Network errors are retryable
      return statusCode >= 500 && statusCode < 600; // All 5xx errors are retryable
    };

    let lastError: { type: 'http' | 'network', statusCode?: number, message?: string } | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(this.apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        // Success!
        if (response.ok) {
          if (this.debug) {
            const result = await response.json();
            console.log('[Analytics] Backend tracking success:', result);
          }
          return;
        }

        // HTTP error response
        lastError = { type: 'http', statusCode: response.status };

        // Retry on 5xx errors (500, 502, 503, etc.)
        if (isRetryableError(response.status) && attempt < maxRetries) {
          const delay = getRetryDelay(attempt);
          if (this.debug) {
            console.log(`[Analytics] HTTP ${response.status}, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
          }
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }

        // Non-retryable error (4xx) - log and exit
        if (this.debug) {
          console.warn(`[Analytics] Non-retryable HTTP ${response.status}, giving up`);
        }
        return;

      } catch (error) {
        // Network/fetch error (CORS, connection refused, timeout, etc.)
        lastError = { type: 'network', message: error instanceof Error ? error.message : String(error) };

        // Retry on last attempt
        if (attempt < maxRetries) {
          const delay = getRetryDelay(attempt);
          if (this.debug) {
            console.log(`[Analytics] Network error, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
          }
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }

        // Last attempt - report to Sentry
        if (this.debug) {
          console.error('[Analytics] Backend tracking failed after all retries:', error);
        }

        if (error instanceof Error) {
          Sentry.captureException(error, {
            level: 'warning',
            tags: {
              error_type: 'event_submitting_to_backend_error',
              event_type: eventType
            },
            extra: {
              event_data: data,
              attempts: attempt + 1,
              last_error: lastError
            }
          });
        }
      }
    }

    // Exhausted all retries on HTTP error
    if (lastError?.type === 'http' && lastError.statusCode) {
      Sentry.captureMessage(`Analytics HTTP ${lastError.statusCode} after ${maxRetries + 1} attempts`, {
        level: 'warning',
        tags: {
          error_type: 'analytics_http_error',
          event_type: eventType,
          status_code: lastError.statusCode.toString()
        },
        extra: {
          event_data: data,
          attempts: maxRetries + 1
        }
      });
    }
  }

  /**
   * Generate a session ID
   */
  private generateSessionId(): string {
    return `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get user ID from the auth state stored by AuthContext.
   * Returns the user's email if logged in, otherwise 'anonymous'.
   */
  private getUserId(): string {
    try {
      const stored = localStorage.getItem('user');
      if (!stored) return 'anonymous';
      const user = JSON.parse(stored) as { email?: string };
      return user.email || 'anonymous';
    } catch {
      return 'anonymous';
    }
  }

  // ============ SEMANTIC METHODS FOR COMMON EVENTS ============

  /**
   * Track page views
   */
  pageView(path: string, title: string, metadata: EventMetadata = {}): void {
    this.push('page_view', {
      page_path: path,
      page_title: title,
      page_location: window.location.href,
      page_referrer: document.referrer,
      ...metadata
    });
  }

  /**
   * Track content clicks/interactions
   */
  clickContent(category: string, itemId: string, itemName: string, metadata: EventMetadata = {}): void {
    this.push('content_click', {
      content_category: category,
      content_id: itemId,
      content_name: itemName,
      ...metadata
    });
  }

  /**
   * Track user engagement events
   */
  engagement(action: string, category: string, metadata: EventMetadata = {}): void {
    this.push('engagement', {
      engagement_action: action,
      engagement_category: category,
      ...metadata
    });
  }

  /**
   * Track search events
   */
  search(query: string, results = 0, metadata: EventMetadata = {}): void {
    this.push('search', {
      search_term: query,
      search_results: results,
      ...metadata
    });
  }

  /**
   * Track errors
   */
  error(errorType: string, errorMessage: string, metadata: EventMetadata = {}): void {
    this.push('error', {
      error_type: errorType,
      error_message: errorMessage,
      page_path: window.location.pathname,
      ...metadata
    });
  }

  /**
   * Track invalid session access attempts
   */
  invalidSession(sessionId: string, metadata: EventMetadata = {}): void {
    this.push('custom', {
      custom_event: 'invalid_session',
      session_id: sessionId,
      attempted_url: window.location.pathname,
      referrer: document.referrer,
      ...metadata
    });
  }

  // ============ UTILITY METHODS ============

  /**
   * Enable/disable analytics
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    if (this.debug) {
      console.log('Analytics:', enabled ? 'enabled' : 'disabled');
    }
  }

  /**
   * Enable/disable debug mode
   */
  setDebug(debug: boolean): void {
    this.debug = debug;
  }

  /**
   * Wait for all pending analytics requests to complete (E2E testing utility)
   * This is only useful in E2E test environments with the pending counter enabled
   */
  async waitForFlush(timeoutMs = 30000): Promise<void> {
    const startTime = Date.now();

    return new Promise((resolve, reject) => {
      const checkPending = () => {
        const pending = window.__e2e_analytics_pending__ || 0;

        // All requests completed
        if (pending === 0) {
          resolve();
          return;
        }

        // Timeout exceeded
        if (Date.now() - startTime > timeoutMs) {
          reject(new Error(`Analytics flush timeout after ${timeoutMs}ms (${pending} requests still pending)`));
          return;
        }

        // Check again in 100ms
        setTimeout(checkPending, 100);
      };

      checkPending();
    });
  }

  /**
   * Get buffered test events for verification (E2E testing utility)
   * Returns all events that were sent to the backend with their full payloads
   *
   * @returns Array of buffered events with eventType, payload, and timestamp
   */
  getTestEvents(): Array<{ eventType: EventType; payload: Partial<EventRequest>; timestamp: number }> {
    return window.__e2e_analytics_buffer__ || [];
  }

  /**
   * Clear the buffered test events (E2E testing utility)
   * Call this between tests to reset the buffer
   */
  clearTestEvents(): void {
    window.__e2e_analytics_buffer__ = [];
    console.log('[Analytics] Test event buffer cleared');
  }
}

// Export singleton instance
const analytics = new Analytics();

// Expose to window for E2E tests
if (typeof window !== 'undefined') {
  window.__analytics__ = analytics;
}

export default analytics;
